import { motion } from 'framer-motion';
import { Shield, Heart, Info, Phone, MapPin, AlertTriangle } from 'lucide-react';

export default function ServicesSafety() {
  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="services-safety-page">
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="badge mb-4">
              Event Details
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ff-brown">
              Services & <span className="text-ff-brown">Safety</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Emergency Info */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8 border-l-4 border-red-500">
              <div className="flex items-center gap-4 mb-6">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <h2 className="font-display text-2xl text-ff-brown">In an Emergency</h2>
              </div>
              <p className="text-ff-brown/70 mb-4">
                For any emergency, locate the nearest crew member or security guard, or head 
                directly to the First Aid tent near the main stage area. Our team operates 24/7.
              </p>
              <p className="text-ff-brown font-medium">
                Emergency Services: 000 (Triple Zero)
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Heart className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">First Aid</h3>
                <p className="text-ff-brown/70 text-sm">
                  Qualified first aid staff are on site 24/7. The main First Aid tent is 
                  located near the info tent and is clearly signposted. For non-emergencies, 
                  visit during quieter hours.
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Shield className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Security</h3>
                <p className="text-ff-brown/70 text-sm">
                  Security is present 24/7 for your safety. If you feel unsafe or witness 
                  concerning behavior, please find security or crew immediately. We have a 
                  zero-tolerance policy for harassment.
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Info className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Info Tent</h3>
                <p className="text-ff-brown/70 text-sm">
                  Your one-stop-shop for information, lost property, wristband issues, 
                  and general assistance. Located near the main entrance and open during 
                  daylight hours.
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Phone className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Phone Charging</h3>
                <p className="text-ff-brown/70 text-sm">
                  Limited phone charging may be available at the info tent. We recommend 
                  bringing your own power bank. Note: mobile reception may be limited.
                </p>
              </div>
            </div>

            {/* On-Site Services */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <h2 className="font-display text-2xl text-ff-brown mb-6">On-Site Services</h2>
              <div className="grid md:grid-cols-2 gap-y-4 text-ff-brown/70">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Toilet facilities (porta-loos)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Showers (limited, may have queues)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Water refill stations</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Food vendors</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Licensed bar</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Market stalls</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Healing/wellness area</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  <span>Ice sales</span>
                </div>
              </div>
            </div>

            {/* Safety Tips */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8">
              <h2 className="font-display text-2xl text-ff-brown mb-6">Safety Tips</h2>
              <ul className="text-ff-brown/70 space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-ff-brown">•</span>
                  Stay hydrated – drink plenty of water, especially when dancing
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-ff-brown">•</span>
                  Look after your mates – check in on each other regularly
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-ff-brown">•</span>
                  Wear sunscreen during the day and warm layers at night
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-ff-brown">•</span>
                  Use a torch at night – watch your footing on uneven ground
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-ff-brown">•</span>
                  Keep valuables secure and don't leave them unattended
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-ff-brown">•</span>
                  Know where the exits and first aid are located
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-ff-brown">•</span>
                  If you see something concerning, say something
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
